import {
  useAppDispatch,
  useExtendedBasketData,
  useUpdateBasketPickAndMixDeviation,
} from "@chef/state-management/hooks";
import { openModal, usePickAndMixQuery } from "@chef/state-management";
import {
  findFeaturedPortraitImage,
  getRecipeFromPickAndMix,
} from "@chef/state-management/helpers";
import { BASE_64_1X1_GREY, PRODUCT_TYPE_IDS } from "@chef/constants";
import { Trash } from "@chef/icons/small";
import { nonNullable } from "@chef/utils/nonNullable";
import { isEqualStrings } from "@chef/utils/equal";
import { ProductCard } from "./ProductCard";

interface RemoveRecipeButtonProps {
  productId: string;
  onRemove: (productId: string) => void;
}

const RemoveRecipeButton = ({
  productId,
  onRemove,
}: RemoveRecipeButtonProps) => (
  <button onClick={() => onRemove(productId)} className="ml-auto">
    <Trash className="relative p-1 left-1 w-7 h-7" />
  </button>
);

interface PickAndMixBasketListProps {
  week: number;
  year: number;
}

export const PickAndMixBasketList = ({
  week,
  year,
}: PickAndMixBasketListProps) => {
  const dispatch = useAppDispatch();

  const { updateBasketPickAndMixDeviation } =
    useUpdateBasketPickAndMixDeviation({ week, year });
  const { data: extendedBasketData } = useExtendedBasketData({ week, year });

  const { data: pickAndMixQuery } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const selectedProducts = extendedBasketData?.selectedProducts;

  if (!pickAndMixQuery || !selectedProducts) {
    return null;
  }

  const pickAndMix = pickAndMixQuery.pickAndMix;

  const pickAndMixProducts = selectedProducts.filter(({ productTypeId }) =>
    isEqualStrings(productTypeId, PRODUCT_TYPE_IDS.PICKANDMIX),
  );

  const productsWithRecipes = pickAndMixProducts.map((product) => {
    const recipe = getRecipeFromPickAndMix({
      pickAndMix,
      productId: product.productId,
      variationId: product.variationId,
    });

    if (!recipe) {
      return null;
    }

    return {
      ...product,
      recipe,
    };
  });

  const displayableRecipes = productsWithRecipes.filter(nonNullable);
  const portions = extendedBasketData.portions;

  const openRecipeModal = (recipeId: number) => {
    dispatch(
      openModal({
        id: "RECIPE",
        recipeId,
        view: "deviation",
        misc: {
          portionSize: portions.toString(),
        },
      }),
    );
  };

  const handleRemoveRecipe = (productId: string) => {
    updateBasketPickAndMixDeviation({
      basketProducts: selectedProducts.filter(
        (product) => product.productId !== productId,
      ),
      portions,
    });
  };

  return (
    <div className="grid grid-cols-1 gap-2">
      {displayableRecipes.map(({ recipe, productId, variationId }) => {
        const imageUrl =
          findFeaturedPortraitImage(recipe)?.url || BASE_64_1X1_GREY;
        return (
          <ProductCard
            key={variationId}
            imageUrl={imageUrl}
            title={recipe.recipeName}
            onClick={() => openRecipeModal(recipe.recipeId)}
          >
            <RemoveRecipeButton
              productId={productId}
              onRemove={handleRemoveRecipe}
            />
          </ProductCard>
        );
      })}
    </div>
  );
};
